import type { PlayerEventName } from '../types'

type Handler = (payload?: unknown) => void

export class EventBus {
  private handlers = new Map<string, Set<Handler>>()
  private host: HTMLElement | null = null
  private enabled = true
  private milestones = new Set<number>()
  private watchTime = 0
  private lastTick: number | null = null
  private heatmap: number[] = Array.from({ length: 20 }, () => 0)
  private heatmapKey: string | null = null

  setHost(el: HTMLElement | null) {
    this.host = el
  }

  setEnabled(on: boolean) {
    this.enabled = on
  }

  setHeatmapKey(key: string | null) {
    this.heatmapKey = key
    if (!key) return
    try {
      const raw = localStorage.getItem(`playerv.heatmap.${key}`)
      if (raw) {
        const parsed = JSON.parse(raw) as number[]
        if (Array.isArray(parsed) && parsed.length === 20) this.heatmap = parsed
      }
    } catch {
      /* ignore */
    }
  }

  on(event: PlayerEventName | string, fn: Handler) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set())
    this.handlers.get(event)!.add(fn)
    return () => this.off(event, fn)
  }

  off(event: PlayerEventName | string, fn: Handler) {
    this.handlers.get(event)?.delete(fn)
  }

  emit(event: PlayerEventName | string, payload?: unknown) {
    if (!this.enabled && event !== 'error') return
    this.handlers.get(event)?.forEach((fn) => {
      try {
        fn(payload)
      } catch {
        /* ignore listener errors */
      }
    })
    if (this.host) {
      this.host.dispatchEvent(
        new CustomEvent(`playerv:${event}`, { detail: payload, bubbles: true }),
      )
    }
  }

  notePlay() {
    this.lastTick = performance.now()
    this.emit('play')
  }

  notePause() {
    this.flushWatch()
    this.emit('pause')
  }

  noteTime(currentTime: number, duration: number, playing: boolean) {
    if (playing) {
      const now = performance.now()
      if (this.lastTick != null) {
        this.watchTime += (now - this.lastTick) / 1000
      }
      this.lastTick = now
      if (duration > 0) {
        const bucket = Math.min(19, Math.floor((currentTime / duration) * 20))
        this.heatmap[bucket] += 1
        this.persistHeatmap()
      }
    }
    this.emit('time', { currentTime, duration, watchTime: this.watchTime })
    if (duration > 0) {
      const pct = (currentTime / duration) * 100
      for (const m of [25, 50, 75]) {
        if (pct >= m && !this.milestones.has(m)) {
          this.milestones.add(m)
          this.emit('progress', { percent: m, currentTime, duration })
        }
      }
    }
  }

  noteComplete() {
    this.flushWatch()
    this.emit('complete', { watchTime: this.watchTime })
  }

  getWatchTime() {
    return this.watchTime
  }

  getHeatmap() {
    return [...this.heatmap]
  }

  resetMilestones() {
    this.milestones.clear()
    this.watchTime = 0
    this.lastTick = null
  }

  private flushWatch() {
    if (this.lastTick != null) {
      this.watchTime += (performance.now() - this.lastTick) / 1000
      this.lastTick = null
    }
  }

  private persistHeatmap() {
    if (!this.heatmapKey) return
    try {
      localStorage.setItem(`playerv.heatmap.${this.heatmapKey}`, JSON.stringify(this.heatmap))
    } catch {
      /* ignore */
    }
  }
}
